import { useEffect } from 'react';
import type { AgedResponse, XeroReportResponse } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';
import { presets, type DateRange } from './SummaryScreen.js';
import { XeroReportTable } from './XeroReportTable.js';
import { AgedTable } from './AgedTable.js';

/**
 * The reports a small company actually opens.
 *
 * The list is the practice's, taken from what the client already stars in
 * Xero, rather than from what the API happens to make easy. Two of them —
 * the aged summaries — have no Xero endpoint at all: its aged reports take
 * a *required* contactId, so a whole-company summary would be one call per
 * customer against a limit of sixty a minute. Those two are worked out from
 * the outstanding documents instead.
 *
 * Each screen says which it is. "Where did this number come from" is a fair
 * question to ask of a report, and a client who cannot tell our arithmetic
 * from Xero's cannot answer it.
 *
 * THE DETAIL REPORTS ARE NOT IN THIS LIST ON PURPOSE
 * Aged receivables and payables detail exist, and they are reached by
 * clicking a name on the matching summary rather than from the menu. One
 * contact per call is the only shape Xero has; a menu entry would promise a
 * whole-company report that cannot be built.
 *
 * NOT HERE ANY MORE: SALES TAX
 * Qatar has no VAT return to file today, and the report was not on the list
 * the practice uses. Its route and arithmetic are still in the API, tested
 * and correct, so the day that changes it is a menu entry rather than a
 * rebuild. See api/src/functions/reports.ts.
 */

export type ReportKind =
  | 'pl'
  | 'balance'
  | 'receivables'
  | 'payables'
  | 'receivables-detail'
  | 'payables-detail';

export const REPORT_TITLES: Record<ReportKind, string> = {
  pl: 'Profit and loss',
  balance: 'Balance sheet',
  receivables: 'Aged receivables summary',
  payables: 'Aged payables summary',
  'receivables-detail': 'Aged receivables detail',
  'payables-detail': 'Aged payables detail',
};

const SUBTITLES: Record<ReportKind, string> = {
  pl: 'What the company earned and spent over a period. Prepared by Xero.',
  balance: 'What the company owns and owes on one day. Prepared by Xero.',
  receivables: 'Who owes this company money, and how late it is.',
  payables: 'Who this company owes, and how late it is.',
  'receivables-detail': 'Every unpaid invoice for one customer. Prepared by Xero.',
  'payables-detail': 'Every unpaid bill for one supplier. Prepared by Xero.',
};

/** The profit and loss alone is about a period. Everything else is a moment. */
export function reportNeedsRange(kind: ReportKind): boolean {
  return kind === 'pl';
}

/** The two reached by clicking a name rather than from the menu. */
export function isDetailReport(kind: ReportKind): boolean {
  return kind === 'receivables-detail' || kind === 'payables-detail';
}

/** The comparisons worth one click on a profit and loss. */
const COMPARISONS: { periods: number; label: string }[] = [
  { periods: 0, label: 'No comparison' },
  { periods: 3, label: 'Last 3 months' },
  { periods: 6, label: 'Last 6 months' },
  { periods: 11, label: 'Last 12 months' },
];

export function ReportsScreen({
  kind,
  range,
  onRange,
  periods,
  onPeriods,
  filter,
  onFilter,
  xeroReport,
  aged,
  detailOf,
  onBack,
  onDrillInto,
  loading,
  error,
  t,
}: {
  kind: ReportKind;
  range: DateRange;
  onRange: (range: DateRange) => void;
  /** Comparison periods on the profit and loss. 0 is a single column. */
  periods: number;
  onPeriods: (periods: number) => void;
  /** Narrows the aged summaries to a name. */
  filter: string;
  onFilter: (filter: string) => void;
  /** Whichever Xero report this screen is showing: P&L, balance, aged detail. */
  xeroReport: XeroReportResponse | null;
  aged: AgedResponse | null;
  /** The contact a detail report is about, for the heading and the way back. */
  detailOf: string | null;
  onBack: () => void;
  /** Opens the detail report for one name on an aged summary. */
  onDrillInto: (contactId: string, contact: string) => void;
  loading: boolean;
  error: string | null;
  t: Translate;
}) {
  const showRange = reportNeedsRange(kind);
  const detail = isDetailReport(kind);

  const field = {
    height: '34px',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  /*
    Changing report clears the filter.

    A name typed to narrow the receivables means nothing on the tax report,
    and a filter still in force from a screen you have left is how a report
    comes to show less than it should without saying why.
  */
  useEffect(() => {
    onFilter('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1040px' }}>
      {/*
        A detail report has no menu entry to go back to, so it carries its
        own way out. Named with the contact, because "Back" on its own
        leaves the reader guessing where back is.
      */}
      {detail && (
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 0,
            padding: 0,
            marginBottom: '10px',
            color: theme.textMuted,
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t(
            kind === 'receivables-detail'
              ? 'Back to aged receivables summary'
              : 'Back to aged payables summary',
          )}
        </button>
      )}

      <PageTitle
        title={detail && detailOf ? detailOf : t(REPORT_TITLES[kind])}
        sub={t(SUBTITLES[kind])}
      />

      {showRange && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            margin: '16px 0',
          }}
        >
          {presets().map((preset) => {
            const picked = preset.range.from === range.from && preset.range.to === range.to;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => onRange(preset.range)}
                style={{
                  background: picked ? theme.nav : 'transparent',
                  color: picked ? '#fff' : theme.text,
                  border: `1px solid ${picked ? theme.nav : theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '8px 14px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t(preset.label)}
              </button>
            );
          })}

          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => onRange({ ...range, from: e.target.value })}
            style={field}
          />
          <span style={{ color: theme.textMuted, fontSize: '12.5px' }}>{t('to')}</span>
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => onRange({ ...range, to: e.target.value })}
            style={field}
          />
        </div>
      )}

      {/*
        Comparison, on the one report Xero can do it for.

        The columns come from Xero with their own arithmetic behind each, so
        this is a parameter rather than something worked out here. See
        api/src/functions/reports.ts.
      */}
      {kind === 'pl' && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 600, color: theme.label }}>
            {t('Compare')}
          </span>
          {COMPARISONS.map((option) => {
            const picked = option.periods === periods;
            return (
              <button
                key={option.periods}
                type="button"
                onClick={() => onPeriods(option.periods)}
                style={{
                  background: picked ? theme.nav : 'transparent',
                  color: picked ? '#fff' : theme.text,
                  border: `1px solid ${picked ? theme.nav : theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '7px 13px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t(option.label)}
              </button>
            );
          })}
        </div>
      )}

      {/* One filter row, above the report it narrows. */}
      {(kind === 'receivables' || kind === 'payables') && (
        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            placeholder={t('Narrow to one name…')}
            style={{ ...field, width: '280px' }}
          />
          {filter !== '' && (
            <button
              type="button"
              onClick={() => onFilter('')}
              style={{
                background: 'transparent',
                border: 0,
                color: theme.textMuted,
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Clear')}
            </button>
          )}
        </div>
      )}

      {!showRange && kind !== 'receivables' && kind !== 'payables' && (
        <div style={{ height: '16px' }} />
      )}

      {/*
        The balance sheet is struck on a day, so it gets a day and not the
        range presets above. Reusing those would ask for a period on a
        report that does not have one.
      */}
      {kind === 'balance' && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 600, color: theme.label }}>
            {t('As at')}
          </span>
          <input
            type="date"
            value={range.to}
            onChange={(e) => onRange({ ...range, to: e.target.value })}
            style={field}
          />
        </div>
      )}

      {error && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(error)}
          </div>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Reading from Xero…')}
          </div>
        </Card>
      )}

      {!loading && !error && kind === 'pl' && xeroReport && (
        <XeroReportTable data={xeroReport} t={t} />
      )}

      {/*
        No charts on these two. The chart above the table reads the last
        total row as a bottom line, which on a balance sheet is assets
        equalling liabilities plus equity — a figure that is always true
        and says nothing — and on an aged detail is one contact's balance.
      */}
      {!loading && !error && (kind === 'balance' || detail) && xeroReport && (
        <XeroReportTable data={xeroReport} charts={false} t={t} />
      )}

      {!loading && !error && (kind === 'receivables' || kind === 'payables') && aged && (
        <AgedTable
          direction={kind === 'receivables' ? 'receivable' : 'payable'}
          data={aged}
          filter={filter}
          onOpen={onDrillInto}
          t={t}
        />
      )}
    </div>
  );
}
